import { StyleSheet, css } from 'aphrodite'
import { useCallback, useEffect, useMemo, useState } from 'react'
import GooglePlacesAutocomplete from 'react-google-places-autocomplete'
import { DOWNSTREAM_PROJECT_IMMUTABLE_FIELDS } from '../../config/templateConfig'
import { projectService } from '../../services/projectService'
import { googlePlacesStyles } from '../../styles/formStyles'
import { IntervalType } from '../../types/project'
import type { Attachment, Project } from '../../types/project'
import DatePicker from '../DatePicker'
import GooglePlacesSelect from '../GooglePlacesSelect'
import MultiSelect from '../MultiSelect'
import type { Option } from '../MultiSelect'
import NoteAttachments from '../NoteAttachments'

type GooglePlaceOption = { label: string; value: string }

type TruckType =
  | 'standard'
  | 'super'
  | 'winter'
  | 'transfer'
  | 'side'
  | 'haul'
  | 'semi_end'
  | 'semi_bottom'

interface JobDetailsStepProps {
  onUpdate: (data: Partial<Project>) => void
  data: Partial<Project>
  readOnly?: boolean
  onValidationChange?: (isValid: boolean) => void
  showErrors?: boolean
  projectId?: number // If provided, enables immediate upload mode
  onAttachmentsChange?: (attachments: Attachment[]) => void // Callback to refresh attachments after upload/delete
  pendingAttachments?: File[] // Pending files for new projects (stored in parent)
  onPendingAttachmentsChange?: (files: File[]) => void // Callback to update pending files in parent
  // Sub-contract flow props
  isSubContract?: boolean
  parentProject?: Project | null
}

const INVOICE_STATUS_OPTIONS = [
  { value: IntervalType.DAILY, label: 'Daily' },
  { value: IntervalType.WEEKLY, label: 'Weekly' },
  { value: IntervalType.BIWEEKLY, label: 'Biweekly' },
  { value: IntervalType.MONTHLY, label: 'Monthly' },
]

const JobDetailsStep: React.FC<JobDetailsStepProps> = ({
  onUpdate,
  data,
  readOnly = false,
  onValidationChange,
  showErrors = false,
  projectId,
  onAttachmentsChange,
  pendingAttachments = [],
  onPendingAttachmentsChange,
  isSubContract = false,
  parentProject = null,
}) => {
  // Helper to check if a field is inherited from parent project (immutable in sub-contract flow)
  const isFieldInherited = (fieldName: string): boolean => {
    return (
      isSubContract &&
      !!parentProject &&
      DOWNSTREAM_PROJECT_IMMUTABLE_FIELDS.includes(
        fieldName as (typeof DOWNSTREAM_PROJECT_IMMUTABLE_FIELDS)[number],
      )
    )
  }

  // Get minimum start date for sub-contracts (parent's start date or today, whichever is later)
  const getMinStartDate = useCallback((): Date => {
    if (isSubContract && parentProject?.starting_date) {
      const parentDate = new Date(parentProject.starting_date)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return parentDate > today ? parentDate : today
    }
    return new Date()
  }, [isSubContract, parentProject?.starting_date])
  const [pickupAddress, setPickupAddress] = useState<GooglePlaceOption | null>(
    data.pickup_address
      ? { label: data.pickup_address, value: data.pickup_address }
      : null,
  )
  const [dropoffAddress, setDropoffAddress] =
    useState<GooglePlaceOption | null>(
      data.dropoff_address
        ? { label: data.dropoff_address, value: data.dropoff_address }
        : null,
    )
  const [thirdPartyLocation, setThirdPartyLocation] =
    useState<GooglePlaceOption | null>(
      data.third_party_location
        ? { label: data.third_party_location, value: data.third_party_location }
        : null,
    )

  // Date state for starting date
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    data.starting_date ? new Date(data.starting_date) : null,
  )
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

  // Date state for completion date
  const [selectedCompletionDate, setSelectedCompletionDate] =
    useState<Date | null>(
      data.completion_date ? new Date(data.completion_date) : null,
    )
  const [isCompletionDatePickerOpen, setIsCompletionDatePickerOpen] =
    useState(false)

  // Set default values for invoice_interval and status_interval
  useEffect(() => {
    const updates: Partial<Project> = {}
    if (!data.invoice_interval) {
      updates.invoice_interval = IntervalType.MONTHLY
    }
    if (!data.status_interval) {
      updates.status_interval = IntervalType.MONTHLY
    }
    if (Object.keys(updates).length > 0) {
      onUpdate(updates)
    }
    // eslint-disable-next-line -- Only run on mount
  }, [onUpdate, data.invoice_interval, data.status_interval])

  // Validation: Check if all required fields are filled
  useEffect(() => {
    const startDate = data.starting_date ? new Date(data.starting_date) : null
    const isValid = Boolean(
      startDate &&
        startDate >= getMinStartDate() &&
        data.pickup_address &&
        data.pickup_address.trim() !== '' &&
        data.dropoff_address &&
        data.dropoff_address.trim() !== '',
    )

    console.log('Project JobDetailsStep validation - data:', {
      starting_date: data.starting_date,
      pickup_address: data.pickup_address,
      dropoff_address: data.dropoff_address,
      isValid,
    })

    if (onValidationChange) {
      onValidationChange(isValid)
    }
  }, [
    data.starting_date,
    data.pickup_address,
    data.dropoff_address,
    onValidationChange,
    getMinStartDate,
  ])

  // Check for individual field errors
  const hasStartingDateError =
    showErrors &&
    (!data.starting_date ||
      data.starting_date.trim() === '' ||
      new Date(data.starting_date) < getMinStartDate())
  const hasPickupAddressError =
    showErrors && (!data.pickup_address || data.pickup_address.trim() === '')
  const hasDropoffAddressError =
    showErrors && (!data.dropoff_address || data.dropoff_address.trim() === '')

  // Update starting_date when date changes
  useEffect(() => {
    // Only update if user has selected a date
    if (!selectedDate) return

    const dateString = selectedDate.toISOString()
    if (dateString !== data.starting_date) {
      onUpdate({ starting_date: dateString })
    }
  }, [selectedDate, data.starting_date, onUpdate])

  // Update completion_date when completion date changes
  useEffect(() => {
    if (selectedCompletionDate) {
      const completionDateTime = selectedCompletionDate.toISOString()
      if (completionDateTime !== data.completion_date) {
        onUpdate({ completion_date: completionDateTime })
      }
    }
  }, [selectedCompletionDate, data.completion_date, onUpdate])

  const truckTypeOptions: Option[] = [
    { value: 'standard', label: 'Standard' },
    { value: 'super', label: 'Super' },
    { value: 'winter', label: 'Winter' },
    { value: 'transfer', label: 'Transfer' },
    { value: 'side', label: 'Side' },
    { value: 'haul', label: 'Haul' },
    { value: 'semi_end', label: 'Semi End' },
    { value: 'semi_bottom', label: 'Semi Bottom' },
  ]

  const handleDateSelect = (date: Date | null) => {
    setSelectedDate(date)
  }

  const handleDateConfirm = () => {
    setIsDatePickerOpen(false)
  }

  const handleCompletionDateSelect = (date: Date | null) => {
    setSelectedCompletionDate(date)
  }

  const handleCompletionDateConfirm = () => {
    setIsCompletionDatePickerOpen(false)
  }

  // Handle file upload for existing projects
  const handleUpload = useCallback(
    async (files: File[]) => {
      if (!projectId) return
      const newAttachments = await projectService.uploadAttachments(
        projectId,
        files,
      )
      // Refresh attachments in parent
      if (onAttachmentsChange && data.attachments) {
        onAttachmentsChange([...data.attachments, ...newAttachments])
      }
    },
    [projectId, onAttachmentsChange, data.attachments],
  )

  // Handle file deletion for existing projects
  const handleDelete = useCallback(
    async (attachmentId: number) => {
      if (!projectId) return
      await projectService.deleteAttachment(projectId, attachmentId)
      // Refresh attachments in parent
      if (onAttachmentsChange && data.attachments) {
        onAttachmentsChange(
          data.attachments.filter((a) => a.id !== attachmentId),
        )
      }
    },
    [projectId, onAttachmentsChange, data.attachments],
  )

  // Handle pending files change for new projects
  const handlePendingFilesChange = useCallback(
    (files: File[]) => {
      if (onPendingAttachmentsChange) {
        onPendingAttachmentsChange(files)
      }
    },
    [onPendingAttachmentsChange],
  )

  return (
    <div className={css(styles.container)}>
      <h3 className={css(styles.sectionTitle)}>Job Details</h3>
      <p className={css(styles.requiredNote)}>* Required field</p>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>
          Job Site Name
          {isFieldInherited('job_site') && (
            <span className={css(styles.inheritedBadge)}>From Parent</span>
          )}
        </label>
        <input
          disabled={readOnly || isFieldInherited('job_site')}
          type="text"
          value={data.job_site || ''}
          onChange={(e) => onUpdate({ job_site: e.target.value })}
          placeholder="Enter job site Name"
          className={css(
            styles.input,
            isFieldInherited('job_site') && styles.inheritedInput,
          )}
        />
      </div>

      <div className={css(styles.row)}>
        <div className={css(styles.formGroup, styles.halfWidth)}>
          <label className={css(styles.label)}>
            Job ID
            {isFieldInherited('job_id') && (
              <span className={css(styles.inheritedBadge)}>From Parent</span>
            )}
          </label>
          <input
            disabled={readOnly || isFieldInherited('job_id')}
            type="text"
            value={data.job_id || ''}
            onChange={(e) => onUpdate({ job_id: e.target.value })}
            placeholder="1234567"
            className={css(
              styles.input,
              isFieldInherited('job_id') && styles.inheritedInput,
            )}
          />
        </div>
        <div className={css(styles.formGroup, styles.halfWidth)}>
          <label className={css(styles.label)}>
            PO Number
            {isFieldInherited('po_number') && (
              <span className={css(styles.inheritedBadge)}>From Parent</span>
            )}
          </label>
          <input
            disabled={readOnly || isFieldInherited('po_number')}
            type="text"
            value={data.po_number || ''}
            onChange={(e) => onUpdate({ po_number: e.target.value })}
            placeholder="Enter PO number"
            className={css(
              styles.input,
              isFieldInherited('po_number') && styles.inheritedInput,
            )}
          />
        </div>
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Starting Date *</label>
        <div className={css(hasStartingDateError && styles.errorHighlight)}>
          <input
            type="text"
            value={selectedDate ? selectedDate.toLocaleDateString() : ''}
            onClick={() => !readOnly && setIsDatePickerOpen(true)}
            placeholder="Select start date"
            readOnly
            className={css(styles.input, readOnly && styles.disabled)}
          />
        </div>
        <DatePicker
          selected={selectedDate}
          onChange={handleDateSelect}
          onSelect={handleDateConfirm}
          minDate={getMinStartDate()}
          isOpen={isDatePickerOpen}
          onClose={() => setIsDatePickerOpen(false)}
        />
        {isSubContract && parentProject?.starting_date && (
          <div className={css(styles.helperText)}>
            Parent project starts:{' '}
            {new Date(parentProject.starting_date).toLocaleDateString()}
          </div>
        )}
        {hasStartingDateError && (
          <div className={css(styles.errorMessage)}>
            {!data.starting_date || data.starting_date.trim() === ''
              ? 'Please select a starting date.'
              : 'Start date cannot be in the past.'}
          </div>
        )}
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Completion Date</label>
        <input
          type="text"
          value={
            selectedCompletionDate
              ? selectedCompletionDate.toLocaleDateString()
              : ''
          }
          onClick={() => !readOnly && setIsCompletionDatePickerOpen(true)}
          placeholder="Select completion date"
          readOnly
          className={css(styles.input, readOnly && styles.disabled)}
        />
        <DatePicker
          selected={selectedCompletionDate}
          onChange={handleCompletionDateSelect}
          onSelect={handleCompletionDateConfirm}
          minDate={selectedDate || new Date()} // Can't be before start date
          isOpen={isCompletionDatePickerOpen}
          onClose={() => setIsCompletionDatePickerOpen(false)}
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>
          Pick Up Location *
          {isFieldInherited('pickup_address') && (
            <span className={css(styles.inheritedBadge)}>From Parent</span>
          )}
        </label>
        {isFieldInherited('pickup_address') ? (
          <input
            type="text"
            value={data.pickup_address || ''}
            disabled
            className={css(styles.input, styles.inheritedInput)}
          />
        ) : (
          <>
            <div
              className={css(hasPickupAddressError && styles.errorHighlight)}
            >
              <GooglePlacesSelect
                value={pickupAddress}
                onChange={(value) => {
                  setPickupAddress(value)
                  onUpdate({ pickup_address: value?.value || '' })
                }}
                isDisabled={readOnly}
                placeholder="Enter pick up address"
              />
            </div>
            {hasPickupAddressError && (
              <div className={css(styles.errorMessage)}>
                Please enter a pick up location.
              </div>
            )}
          </>
        )}
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>
          Drop Off Location *
          {isFieldInherited('dropoff_address') && (
            <span className={css(styles.inheritedBadge)}>From Parent</span>
          )}
        </label>
        {isFieldInherited('dropoff_address') ? (
          <input
            type="text"
            value={data.dropoff_address || ''}
            disabled
            className={css(styles.input, styles.inheritedInput)}
          />
        ) : (
          <>
            <div
              className={css(hasDropoffAddressError && styles.errorHighlight)}
            >
              <GooglePlacesSelect
                value={dropoffAddress}
                onChange={(value) => {
                  setDropoffAddress(value)
                  onUpdate({ dropoff_address: value?.value || '' })
                }}
                isDisabled={readOnly}
                placeholder="Enter drop off address"
              />
            </div>
            {hasDropoffAddressError && (
              <div className={css(styles.errorMessage)}>
                Please enter a drop off location.
              </div>
            )}
          </>
        )}
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>
          Third Party Location
          {isFieldInherited('third_party_location') && (
            <span className={css(styles.inheritedBadge)}>From Parent</span>
          )}
        </label>
        {isFieldInherited('third_party_location') ? (
          <input
            type="text"
            value={data.third_party_location || ''}
            disabled
            className={css(styles.input, styles.inheritedInput)}
          />
        ) : (
          <GooglePlacesSelect
            value={thirdPartyLocation}
            onChange={(value) => {
              setThirdPartyLocation(value)
              onUpdate({ third_party_location: value?.value || '' })
            }}
            isDisabled={readOnly}
            placeholder="Enter third party location"
          />
        )}
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>
          Material(s)
          {isFieldInherited('material') && (
            <span className={css(styles.inheritedBadge)}>From Parent</span>
          )}
        </label>
        <input
          type="text"
          value={data.material || ''}
          onChange={(e) => onUpdate({ material: e.target.value })}
          placeholder="Enter material type"
          className={css(
            styles.input,
            isFieldInherited('material') && styles.inheritedInput,
          )}
          disabled={readOnly || isFieldInherited('material')}
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Number of Trucks</label>
        <input
          type="text"
          value={data.trucks_number || ''}
          onChange={(e) => onUpdate({ trucks_number: e.target.value })}
          placeholder="Enter number of trucks"
          className={css(styles.input)}
          disabled={readOnly}
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>
          Truck Types
          {isFieldInherited('truck_types') && (
            <span className={css(styles.inheritedBadge)}>From Parent</span>
          )}
        </label>
        <MultiSelect
          options={truckTypeOptions}
          value={
            data.truck_types?.map((type) => ({ value: type, label: type })) ||
            []
          }
          onChange={(selected) => {
            if (Array.isArray(selected)) {
              onUpdate({ truck_types: selected.map((v) => v.value) })
            } else {
              onUpdate({ truck_types: [] })
            }
          }}
          isDisabled={readOnly || isFieldInherited('truck_types')}
        />
      </div>

      <div className={css(styles.row)}>
        <div className={css(styles.formGroup, styles.halfWidth)}>
          <label className={css(styles.label)}>Invoices</label>
          <MultiSelect
            isMulti={false}
            isDisabled={readOnly}
            placeholder="Select interval"
            options={INVOICE_STATUS_OPTIONS}
            value={
              data.invoice_interval
                ? {
                    value: data.invoice_interval,
                    label:
                      INVOICE_STATUS_OPTIONS.find(
                        (opt) => opt.value === data.invoice_interval,
                      )?.label || '',
                  }
                : undefined
            }
            onChange={(option) => {
              const singleOption =
                option && !Array.isArray(option) ? option : null
              onUpdate({
                invoice_interval: singleOption
                  ? (singleOption.value as IntervalType)
                  : undefined,
              })
            }}
          />
        </div>
        <div className={css(styles.formGroup, styles.halfWidth)}>
          <label className={css(styles.label)}>Status Reports</label>
          <MultiSelect
            isMulti={false}
            isDisabled={readOnly}
            placeholder="Select interval"
            options={INVOICE_STATUS_OPTIONS}
            value={
              data.status_interval
                ? {
                    value: data.status_interval,
                    label:
                      INVOICE_STATUS_OPTIONS.find(
                        (opt) => opt.value === data.status_interval,
                      )?.label || '',
                  }
                : undefined
            }
            onChange={(option) => {
              const singleOption =
                option && !Array.isArray(option) ? option : null
              onUpdate({
                status_interval: singleOption
                  ? (singleOption.value as IntervalType)
                  : undefined,
              })
            }}
          />
        </div>
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Notes</label>
        <textarea
          disabled={readOnly}
          value={data.notes || ''}
          onChange={(e) => onUpdate({ notes: e.target.value })}
          placeholder="Enter any additional notes or special instructions"
          className={css(styles.textarea)}
          rows={4}
        />
      </div>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Attachments</label>
        <NoteAttachments
          attachments={data.attachments || []}
          pendingFiles={pendingAttachments}
          onPendingFilesChange={handlePendingFilesChange}
          onUpload={projectId ? handleUpload : undefined}
          onDelete={projectId ? handleDelete : undefined}
          readOnly={readOnly}
        />
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '8px',
  },
  requiredNote: {
    fontSize: '12px',
    color: '#6B7280',
    marginBottom: '20px',
    fontStyle: 'italic',
  },
  formGroup: {
    marginBottom: '16px',
    '& .google-places-autocomplete__input': {
      width: '100%',
      padding: '8px 12px',
      border: '1px solid #E5E7EB',
      borderRadius: '6px',
      fontSize: '14px',
    },
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid rgb(209, 213, 219)',
    borderRadius: '6px',
    fontSize: '14px',
  },
  row: {
    display: 'flex',
    gap: '16px',
  },
  halfWidth: {
    flex: 1,
  },
  disabled: {
    backgroundColor: '#F3F4F6',
    cursor: 'not-allowed',
  },
  errorHighlight: {
    borderRadius: '6px',
    boxShadow: '0 0 0 2px #FCD34D',
    backgroundColor: '#FFFBEB',
  },
  errorMessage: {
    color: '#DC2626',
    fontSize: '12px',
    marginTop: '4px',
    fontWeight: 500,
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid rgb(209, 213, 219)',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    minHeight: '80px',
  },
  inheritedBadge: {
    marginLeft: '8px',
    padding: '2px 8px',
    backgroundColor: '#DBEAFE',
    color: '#1D4ED8',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
    verticalAlign: 'middle',
  },
  inheritedInput: {
    backgroundColor: '#F3F4F6',
    color: '#6B7280',
    cursor: 'not-allowed',
    borderColor: '#D1D5DB',
  },
  helperText: {
    fontSize: '12px',
    color: '#6B7280',
    marginTop: '4px',
  },
})

export default JobDetailsStep
